import { useState } from 'react';
import toast from 'react-hot-toast';
import { PiCheckCircle, PiProhibit, PiBriefcase } from 'react-icons/pi';

import { Button, Spinner, Paginator } from '@/components/ui';
import {
    useAdminWorkers,
    useAdminProfessions,
    useApproveWorker,
    useSuspendWorker,
    useSetProfessions,
} from '@/api/admin';
import type { WorkerProfile, Profession } from '@/types/worker';

import styles from './AdminWorkersPage.module.css';

const STATUS_FILTERS = [
    { value: '', label: 'Todos' },
    { value: 'pending', label: 'Pendientes' },
    { value: 'approved', label: 'Aprobados' },
    { value: 'suspended', label: 'Suspendidos' },
] as const;

const PAGE_SIZE = 10;

export function AdminWorkersPage() {
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [professionModal, setProfessionModal] = useState<WorkerProfile | null>(null);
    const [selectedProfessions, setSelectedProfessions] = useState<string[]>([]);

    const offset = (page - 1) * PAGE_SIZE;
    const { data, isLoading } = useAdminWorkers({
        status: statusFilter || undefined,
        limit: PAGE_SIZE,
        offset,
    });
    const { data: allProfessions } = useAdminProfessions();

    const approveWorker = useApproveWorker();
    const suspendWorker = useSuspendWorker();
    const setProfessionsMut = useSetProfessions();

    const workers = data?.results ?? [];
    const totalCount = data?.count ?? 0;
    const numPages = Math.ceil(totalCount / PAGE_SIZE);

    const pendingCount = workers.filter((w) => w.status === 'pending').length;

    const handleApprove = async (worker: WorkerProfile) => {
        try {
            await approveWorker.mutateAsync(worker.uuid);
            toast.success(`${worker.email} aprobado`);
        } catch {
            toast.error('Error al aprobar trabajador');
        }
    };

    const handleSuspend = async (worker: WorkerProfile) => {
        try {
            await suspendWorker.mutateAsync(worker.uuid);
            toast.success(`${worker.email} suspendido`);
        } catch {
            toast.error('Error al suspender trabajador');
        }
    };

    const openProfessionModal = (worker: WorkerProfile) => {
        setSelectedProfessions(worker.professions.map((p) => p.uuid));
        setProfessionModal(worker);
    };

    const handleSaveProfessions = async () => {
        if (!professionModal) return;
        try {
            await setProfessionsMut.mutateAsync({
                uuid: professionModal.uuid,
                professionUuids: selectedProfessions,
            });
            toast.success('Profesiones actualizadas');
            setProfessionModal(null);
        } catch {
            toast.error('Error al actualizar profesiones');
        }
    };

    const toggleProfession = (uuid: string) => {
        setSelectedProfessions((prev) =>
            prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid]
        );
    };

    const getStatusBadge = (status: WorkerProfile['status']) => {
        const map = {
            pending: { className: styles.badgePending, label: 'Pendiente' },
            approved: { className: styles.badgeApproved, label: 'Aprobado' },
            suspended: { className: styles.badgeSuspended, label: 'Suspendido' },
        };
        const config = map[status];
        return <span className={`${styles.badge} ${config.className}`}>{config.label}</span>;
    };

    const getInitials = (worker: WorkerProfile) => {
        if (worker.first_name && worker.last_name) {
            return `${worker.first_name[0]}${worker.last_name[0]}`.toUpperCase();
        }
        return worker.email[0].toUpperCase();
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Gestion de Usuarios</h1>
                <div className={styles.filters}>
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f.value}
                            className={`${styles.filterBtn} ${statusFilter === f.value ? styles.filterActive : ''}`}
                            onClick={() => { setStatusFilter(f.value); setPage(1); }}
                        >
                            {f.label}
                            {f.value === 'pending' && pendingCount > 0 && ` (${pendingCount})`}
                        </button>
                    ))}
                </div>
            </div>

            <div className={styles.stats}>
                <div className={styles.statCard}>
                    <p className={styles.statLabel}>Total trabajadores</p>
                    <p className={styles.statValue}>{totalCount}</p>
                </div>
            </div>

            {isLoading ? (
                <div className={styles.loading}><Spinner size="lg" /></div>
            ) : workers.length === 0 ? (
                <p className={styles.empty}>No se encontraron trabajadores</p>
            ) : (
                <>
                    <div className={styles.workerList}>
                        {workers.map((worker) => (
                            <WorkerCard
                                key={worker.uuid}
                                worker={worker}
                                onApprove={handleApprove}
                                onSuspend={handleSuspend}
                                onEditProfessions={openProfessionModal}
                                getStatusBadge={getStatusBadge}
                                getInitials={getInitials}
                                isApproving={approveWorker.isPending}
                                isSuspending={suspendWorker.isPending}
                            />
                        ))}
                    </div>

                    {numPages > 1 && (
                        <div className={styles.paginatorWrap}>
                            <Paginator
                                page={page}
                                numPages={numPages}
                                onPageChange={setPage}
                                size="sm"
                            />
                        </div>
                    )}
                </>
            )}

            {professionModal && (
                <ProfessionModal
                    worker={professionModal}
                    professions={allProfessions ?? []}
                    selected={selectedProfessions}
                    onToggle={toggleProfession}
                    onSave={handleSaveProfessions}
                    onClose={() => setProfessionModal(null)}
                    isSaving={setProfessionsMut.isPending}
                />
            )}
        </div>
    );
}

function WorkerCard({
    worker,
    onApprove,
    onSuspend,
    onEditProfessions,
    getStatusBadge,
    getInitials,
    isApproving,
    isSuspending,
}: {
    worker: WorkerProfile;
    onApprove: (w: WorkerProfile) => void;
    onSuspend: (w: WorkerProfile) => void;
    onEditProfessions: (w: WorkerProfile) => void;
    getStatusBadge: (s: WorkerProfile['status']) => React.ReactNode;
    getInitials: (w: WorkerProfile) => string;
    isApproving: boolean;
    isSuspending: boolean;
}) {
    return (
        <div className={styles.workerCard}>
            <div className={styles.workerAvatar}>{getInitials(worker)}</div>
            <div className={styles.workerInfo}>
                <p className={styles.workerName}>
                    {worker.first_name || worker.last_name
                        ? `${worker.first_name} ${worker.last_name}`.trim()
                        : 'Sin nombre'}
                </p>
                <p className={styles.workerEmail}>{worker.email}</p>
                <div className={styles.workerMeta}>
                    {getStatusBadge(worker.status)}
                    {worker.professions.map((p) => (
                        <span key={p.uuid} className={styles.professionTag}>{p.name}</span>
                    ))}
                </div>
            </div>
            <div className={styles.workerActions}>
                {worker.status !== 'approved' && (
                    <Button
                        variant="success"
                        size="sm"
                        onClick={() => onApprove(worker)}
                        disabled={isApproving}
                        title="Aprobar"
                    >
                        <PiCheckCircle /> Aprobar
                    </Button>
                )}
                {worker.status !== 'suspended' && (
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onSuspend(worker)}
                        disabled={isSuspending}
                        title="Suspender"
                    >
                        <PiProhibit />
                    </Button>
                )}
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onEditProfessions(worker)}
                    title="Asignar profesiones"
                >
                    <PiBriefcase />
                </Button>
            </div>
        </div>
    );
}

function ProfessionModal({
    worker,
    professions,
    selected,
    onToggle,
    onSave,
    onClose,
    isSaving,
}: {
    worker: WorkerProfile;
    professions: Profession[];
    selected: string[];
    onToggle: (uuid: string) => void;
    onSave: () => void;
    onClose: () => void;
    isSaving: boolean;
}) {
    return (
        <div className={styles.professionModal}>
            <div className={styles.professionOverlay} onClick={onClose} />
            <div className={styles.professionPanel}>
                <h3 className={styles.professionPanelTitle}>
                    Profesiones de {worker.email}
                </h3>
                <div className={styles.professionList}>
                    {professions.length === 0 ? (
                        <p className={styles.empty}>No hay profesiones configuradas</p>
                    ) : (
                        professions.map((p) => (
                            <label
                                key={p.uuid}
                                className={`${styles.professionItem} ${selected.includes(p.uuid) ? styles.professionItemSelected : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    className={styles.professionCheckbox}
                                    checked={selected.includes(p.uuid)}
                                    onChange={() => onToggle(p.uuid)}
                                />
                                <span className={styles.professionItemName}>{p.name}</span>
                            </label>
                        ))
                    )}
                </div>
                <div className={styles.professionActions}>
                    <Button variant="secondary" size="sm" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button variant="primary" size="sm" onClick={onSave} disabled={isSaving}>
                        {isSaving ? <Spinner size="xs" variant="secondary" /> : 'Guardar'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
